// @shared:standard — used by ProjectSettingsModal (un projet) and ConfigurationPage
// (un template de projet). Impact: changes affect both rights screens.
import React from "react";
import {
  GENERAL_COLUMNS,
  TASK_COLUMNS,
  toggleGeneralRight,
  toggleTaskRight,
} from "../../../services/projectRights";

/**
 * Rights-matrix editor for a project group.
 *
 * Config-first note: this is React and not a reactive-json rjBuild because the
 * matrix is NOT entity data — it is the group permissions of the project,
 * served by a dedicated endpoint (`/api/project/{id}/rights`) whose axes
 * (roles × task types) are resolved server-side, and both screens that show it
 * are already React components driving their own save sequence.
 *
 * The columns are the contract of that endpoint; the server maps them onto
 * Drupal group permissions (see ProjectRightsMatrix).
 *
 * Props:
 *   rights       — { roles, types, general, tasks } from the endpoint, or null while loading
 *   onChange     — (nextRights) => void
 *   visibleTypes — optional subset of rights.types (e.g. only the enabled ones)
 *   error        — message to show instead of the tables
 *   emptyLabel   — shown when no task type is visible
 */
const RightsMatrix = ({ rights, onChange, visibleTypes, error = '', emptyLabel = 'Aucun type de tâche activé.' }) => {
  if (error) {
    return (
      <p className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded px-3 py-2" role="alert">
        {error}
      </p>
    );
  }
  if (!rights) {
    return <p className="text-sm text-slate-400 text-center py-8">Chargement de la matrice…</p>;
  }

  const types = visibleTypes ?? rights.types ?? [];

  return (
    <div className="space-y-8">
      <div className="bg-white p-6 rounded-lg shadow-sm border border-slate-200">
        <h3 className="text-lg font-medium text-slate-900 mb-4">Droits Généraux</h3>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-slate-200 text-sm">
            <caption className="sr-only">Droits généraux du projet, par rôle</caption>
            <thead className="bg-slate-50">
              <tr>
                <th scope="col" className="px-4 py-2 text-left font-medium text-slate-500">Rôle</th>
                {GENERAL_COLUMNS.map(column => (
                  <th key={column.key} scope="col" className="px-4 py-2 text-center font-medium text-slate-500">
                    {column.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200">
              {rights.roles.map(role => (
                <tr key={role.id}>
                  <th scope="row" className="px-4 py-2 text-left font-medium text-slate-900">
                    {role.label}
                    {role.admin && (
                      <span className="block text-xs font-normal text-slate-500">Tous les droits</span>
                    )}
                  </th>
                  {GENERAL_COLUMNS.map(column => (
                    <td key={column.key} className="px-4 py-2 text-center">
                      <input
                        type="checkbox"
                        checked={rights.general?.[role.id]?.[column.key] ?? true}
                        disabled={role.admin}
                        aria-label={`${column.label} — ${role.label}`}
                        onChange={() => onChange(toggleGeneralRight(rights, role.id, column.key))}
                        className={`rounded text-iris-600 focus:ring-iris-500 ${role.admin ? 'opacity-50 cursor-not-allowed' : ''}`}
                      />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="bg-white p-6 rounded-lg shadow-sm border border-slate-200">
        <h3 className="text-lg font-medium text-slate-900 mb-4">Droits par Tâche</h3>
        <div className="space-y-6">
          {types.map(type => (
            <div key={type.id} className="border border-slate-200 rounded-lg overflow-hidden">
              <div className="bg-slate-50 px-4 py-2 border-b border-slate-200 font-medium text-slate-700 flex justify-between items-center">
                <span>{type.label}</span>
                <span className="text-xs font-normal text-slate-500">Configuration des droits</span>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-slate-200 text-xs">
                  <caption className="sr-only">{`Droits par rôle pour les tâches de type ${type.label}`}</caption>
                  <thead className="bg-white">
                    <tr>
                      <th scope="col" className="px-3 py-2 text-left font-medium text-slate-500 w-32">Rôle</th>
                      {TASK_COLUMNS.map(column => (
                        <th key={column.key} scope="col" className="px-2 py-2 text-center font-medium text-slate-500">
                          {column.label}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200">
                    {rights.roles.map(role => {
                      const row = rights.tasks?.[role.id]?.[type.id] || {};
                      return (
                        <tr key={role.id} className="hover:bg-slate-50">
                          <th scope="row" className="px-3 py-2 text-left font-medium text-slate-900">{role.label}</th>
                          {TASK_COLUMNS.map(column => {
                            const isDisabled = role.admin;
                            return (
                              <td key={column.key} className="px-2 py-2 text-center">
                                <input
                                  type="checkbox"
                                  checked={row[column.key] ?? true}
                                  disabled={isDisabled}
                                  aria-label={`${column.label} — ${role.label} — ${type.label}`}
                                  onChange={() => onChange(toggleTaskRight(rights, role.id, type.id, column.key))}
                                  className={`rounded text-iris-600 focus:ring-iris-500 h-4 w-4 ${isDisabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                                />
                              </td>
                            );
                          })}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          ))}
          {types.length === 0 && (
            <p className="text-sm text-slate-400 text-center py-8">{emptyLabel}</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default RightsMatrix;
